import {
  ChatTransportError,
  type ChatConversationStatus,
  type ChatMessage,
  type ChatMessageListener,
  type ChatTransport,
  type ChatTransportErrorKind,
  type Unsubscribe,
} from './chatTransport';
import {
  PollingChatConversation,
  type FetchConversationMessages,
  type FetchMessagesOptions,
} from './pollingChatTransport';
import type { PostChatMessage } from './postInquiryMessage';

import { FIVE_SECONDS, TWO_SECONDS } from '@/constants/time';
import type { InboxSignal } from '@/services/conversations/inboxSignals';

/**
 * What a Twilio conversation carries on itself and our API holds on the
 * inquiry instead.
 */
export type PrimumConversationDetails = {
  /** The user ids taking part in the conversation. */
  participantIds: number[];
  /**
   * The conversation's attributes as Twilio holds them: the inquiry's title
   * (cancer focus, age, diagnosis), which the chat header shows as the subject.
   */
  attributes: unknown;
  status: ChatConversationStatus;
};

export type PrimumChatTransportDeps = {
  /** The effective viewer, as a user id string. */
  viewerId: string;
  /** Every message in an inquiry's conversation, from our API. Throws the API's error. */
  fetchMessages: FetchConversationMessages;
  /** What our API holds about an inquiry's conversation. Throws the API's error. */
  fetchConversationDetails: (conversationId: string) => Promise<PrimumConversationDetails>;
  /** Posts the viewer's message. Throws the API's error. */
  postMessage: PostChatMessage;
  /** The app-wide inbox socket's hints. */
  subscribeToSignals: (listener: (signal: InboxSignal) => void) => Unsubscribe;
  /** Whether the inbox socket is connected, so its hints can be relied on. */
  isSignalLive?: () => boolean;
  pollIntervalMs?: number;
  now?: () => number;
};

/**
 * How often an open conversation re-reads itself while the inbox socket is
 * down. While it is live nothing polls: a new message and a change to one
 * (its pipeline status, citations or attachments) are both hinted, and a
 * reconnect resyncs whatever was missed while it was down.
 */
const DEFAULT_POLL_INTERVAL_MS = FIVE_SECONDS;

/**
 * A read this recent makes the read on subscribe redundant. Opening a
 * conversation reads it, and the chat subscribes straight after; reading
 * again then doubled every open.
 */
const FRESH_READ_MS = TWO_SECONDS;

type PrimumConversationOptions = {
  intervalMs: number;
  postMessage: PostChatMessage;
  subscribeToSignals: PrimumChatTransportDeps['subscribeToSignals'];
  isSignalLive: () => boolean;
  now: () => number;
};

function statusOf(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'status' in error) {
    const { status } = error as { status: unknown };

    return typeof status === 'number' ? status : undefined;
  }

  return undefined;
}

function kindForStatus(status: number | undefined): ChatTransportErrorKind {
  if (status === 404) {
    return 'not_found';
  }

  if (status === 403) {
    return 'forbidden';
  }

  return 'connection';
}

function toChatTransportError(error: unknown): ChatTransportError {
  if (error instanceof ChatTransportError) {
    return error;
  }

  const kind = kindForStatus(statusOf(error));

  return new ChatTransportError(
    kind,
    error instanceof Error ? error.message : 'Unknown error',
    error
  );
}

/**
 * An inquiry's conversation read from our own API.
 *
 * It polls only while someone listens: the open chat subscribes for as long as
 * it is mounted, so a conversation the desktop inbox opened and left costs
 * nothing. A hint naming this conversation re-reads it at once, from the
 * index the hint needs rather than from the start, so a read costs what is
 * new. A resync after hints may have been missed, and the poll while the
 * socket is down, read the whole conversation, since a change to an older
 * message is only found that way. Reads never overlap: one requested while
 * another is in flight waits for it, so a read that started before a message
 * changed can never finish after a newer one and replay the old version.
 */
export class PrimumChatConversation extends PollingChatConversation {
  private listenerCount = 0;

  private stopSignals: Unsubscribe | undefined;

  private inFlight: Promise<void> | undefined;

  /** The read still owed once the one in flight finishes, merged across every request for one. */
  private pending: FetchMessagesOptions | undefined;

  /** When the last full read started. A read after an index does not see changes before it. */
  private lastReadAt: number;

  constructor(
    id: string,
    fetchMessages: FetchConversationMessages,
    initialMessages: ChatMessage[],
    private readonly options: PrimumConversationOptions
  ) {
    super(id, fetchMessages, options.intervalMs, initialMessages, options.postMessage);

    this.lastReadAt = options.now();
  }

  override onMessageAdded(listener: ChatMessageListener): Unsubscribe {
    return this.retain(super.onMessageAdded(listener));
  }

  override onMessageUpdated(listener: ChatMessageListener): Unsubscribe {
    return this.retain(super.onMessageUpdated(listener));
  }

  /** Re-reads the whole conversation now, joining a read already in flight. */
  refresh(): Promise<void> {
    return this.poll();
  }

  protected override poll(): Promise<void> {
    return this.request({});
  }

  /** A sent message is past everything held, so the read after a send starts at the newest. */
  protected override afterSend(): void {
    const newest = this.newestIndex();

    this.request(newest === undefined ? {} : { after: newest }).catch(() => undefined);
  }

  /** The poll is only the fallback for a socket that is down. */
  protected override tick(): Promise<void> {
    if (this.options.isSignalLive()) {
      return Promise.resolve();
    }

    return this.poll();
  }

  /**
   * Reads now, or once the read in flight finishes. Requests made meanwhile
   * share one read that covers them all: from the lowest index any asked
   * for, or the whole conversation if any asked for that.
   */
  private request(read: FetchMessagesOptions): Promise<void> {
    this.pending = this.pending ? coverBoth(this.pending, read) : read;

    if (!this.inFlight) {
      this.inFlight = this.readUntilCurrent().finally(() => {
        this.inFlight = undefined;
      });
    }

    return this.inFlight;
  }

  private async readUntilCurrent(): Promise<void> {
    while (this.pending) {
      const read = this.pending;

      this.pending = undefined;

      if (read.after === undefined) {
        this.lastReadAt = this.options.now();
      }

      await super.read(read);
    }
  }

  /**
   * What a hint needs re-read. A new message is past everything held, so the
   * read starts at the newest held; a changed one starts just before it. With
   * nothing held, or a change to the first message, the read is the whole
   * conversation, since the API takes no index below zero.
   */
  private readFor(signal: Exclude<InboxSignal, { type: 'resync' }>): FetchMessagesOptions {
    const newest = this.newestIndex();

    if (newest === undefined) {
      return {};
    }

    if (signal.type === 'message.updated') {
      const before = Math.min(signal.data.index, newest + 1) - 1;

      return before < 0 ? {} : { after: before };
    }

    return { after: newest };
  }

  private newestIndex(): number | undefined {
    return this.messages.reduce<number | undefined>(
      (newest, message) =>
        newest === undefined || message.index > newest ? message.index : newest,
      undefined
    );
  }

  /** Stops everything this conversation started, whoever still listens. */
  dispose(): void {
    this.listenerCount = 0;

    this.pause();
  }

  private retain(unsubscribe: Unsubscribe): Unsubscribe {
    this.listenerCount += 1;

    if (this.listenerCount === 1) {
      this.resume();
    }

    let released = false;

    return () => {
      unsubscribe();

      if (released) {
        return;
      }

      released = true;

      this.listenerCount = Math.max(0, this.listenerCount - 1);

      if (this.listenerCount === 0) {
        this.pause();
      }
    };
  }

  private resume(): void {
    this.start();

    this.stopSignals = this.options.subscribeToSignals((signal) => {
      if (signal.type === 'resync') {
        this.refresh().catch(() => undefined);
      } else if (signal.data.conversationId === this.id) {
        this.request(this.readFor(signal)).catch(() => undefined);
      }
    });

    // Whatever arrived while nobody listened is delivered as new.
    if (this.options.now() - this.lastReadAt >= FRESH_READ_MS) {
      this.refresh().catch(() => undefined);
    }
  }

  private pause(): void {
    this.stop();

    this.stopSignals?.();

    this.stopSignals = undefined;
  }
}

function coverBoth(
  first: FetchMessagesOptions,
  second: FetchMessagesOptions
): FetchMessagesOptions {
  if (first.after === undefined || second.after === undefined) {
    return {};
  }

  return { after: Math.min(first.after, second.after) };
}

/**
 * Inquiry chat on Primum's own messages API, with no Twilio client.
 *
 * Twilio refuses a viewer who is not in the conversation, and that refusal is
 * what makes Primum Support's view of an inquiry read-only. Our API lets staff
 * read any inquiry, so the same rule is applied here from the inquiry's
 * participants: a viewer outside them is refused as `forbidden`, and the chat
 * falls back to the read-only conversation exactly as it does under Twilio.
 *
 * Sending already goes through the API under both transports, and marking
 * read posts to the API from the chat, so neither needs anything here beyond
 * moving the local read horizon.
 */
export class PrimumChatTransport implements ChatTransport {
  private readonly conversations = new Map<string, Promise<PrimumChatConversation>>();

  constructor(private readonly deps: PrimumChatTransportDeps) {}

  getConversation(conversationId: string): Promise<PrimumChatConversation> {
    const existing = this.conversations.get(conversationId);

    if (existing) {
      return existing;
    }

    const opening = this.open(conversationId);

    this.conversations.set(conversationId, opening);

    // A refusal is not remembered, so the next open asks again.
    opening.catch(() => {
      if (this.conversations.get(conversationId) === opening) {
        this.conversations.delete(conversationId);
      }
    });

    return opening;
  }

  /**
   * Drops one conversation and stops what it started. For a caller done with
   * it for good, such as a parallel run that has compared it; the next open
   * reads it afresh.
   */
  forget(conversationId: string): void {
    const opening = this.conversations.get(conversationId);

    this.conversations.delete(conversationId);

    opening?.then((conversation) => conversation.dispose()).catch(() => undefined);
  }

  async shutdown(): Promise<void> {
    const opened = await Promise.allSettled(this.conversations.values());

    this.conversations.clear();

    for (const result of opened) {
      if (result.status === 'fulfilled') {
        result.value.dispose();
      }
    }
  }

  private async open(conversationId: string): Promise<PrimumChatConversation> {
    const { fetchMessages, fetchConversationDetails, viewerId, subscribeToSignals } = this.deps;

    let messages: ChatMessage[];

    let details: PrimumConversationDetails;

    try {
      [messages, details] = await Promise.all([
        fetchMessages(conversationId),
        fetchConversationDetails(conversationId),
      ]);
    } catch (error) {
      throw toChatTransportError(error);
    }

    if (!details.participantIds.some((participantId) => String(participantId) === viewerId)) {
      throw new ChatTransportError('forbidden', 'Not a participant in this conversation');
    }

    const conversation = new PrimumChatConversation(conversationId, fetchMessages, messages, {
      intervalMs: this.deps.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS,
      postMessage: this.deps.postMessage,
      subscribeToSignals,
      isSignalLive: this.deps.isSignalLive ?? (() => false),
      now: this.deps.now ?? Date.now,
    });

    conversation.attributes = details.attributes;

    conversation.status = details.status;

    return conversation;
  }
}
